/**
 * @license Copyright 2019 Google Inc. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except in compliance with the License. You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software distributed under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the License for the specific language governing permissions and limitations under the License.
 */

.build-hash-selector {
  --line-item-height: 40px;

  position: fixed;
  z-index: 10;

  top: var(--page-header-height);
  left: 0;
  right: 0;
  min-height: 10vh;
  max-height: 60vh;

  overflow: auto;

  background: var(--highlighted-background-color);
  box-shadow: 0 1px 1px var(--base-border-color);
}

#storybook-test-root .build-hash-selector {
  top: 0;
  max-height: calc(100vh - 5px);
}

.build-hash-selector-obscure-background {
  position: fixed;
  z-index: 9;

  top: var(--page-header-height);
  bottom: 0;
  left: 0;
  right: 0;

  background: var(--highlighted-background-color);
  opacity: 0.9;
}

.build-hash-selector__list {
  list-style: none;
  padding: 0;
}

.build-hash-selector__item {
  position: relative;
  height: var(--line-item-height);
  width: 100%;
  font-weight: var(--medium-font-weight);
}

.build-hash-selector__item > div.container {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: var(--line-item-height);
  padding: calc(var(--base-spacing) / 2);
}

.build-hash-selector__item:hover,
.build-hash-selector__item--selected {
  cursor: pointer;
  background: var(--compare-highlight-color);
}

.build-hash-selector--base li:hover,
.build-hash-selector--base .build-hash-selector__item--selected {
  cursor: pointer;
  background: var(--neutral-highlight-color);
}

.build-hash-selector__item.build-hash-selector__label-li:hover {
  cursor: initial;
  background: none;
}

.build-hash-selector__selection {
  width: 90px;
  min-width: 90px;
}

.build-hash-selector__selection .pill--compare.pill--solid,
.build-hash-selector__selection .pill--base.pill--solid {
  text-transform: uppercase;
  width: 90%;
  text-align: center;
  font-size: var(--subtext-font-size);
}

.build-hash-selector__selection .pill--compare.pill--solid {
  background: var(--compare-color);
  color: var(--inverted-text-color);
}

.build-hash-selector__selection .pill--base.pill--solid {
  background: var(--neutral-color);
  color: var(--inverted-text-color);
}

.build-hash-selector__selector-selection {
  position: absolute;
  right: calc(var(--base-spacing));
  bottom: calc((var(--line-item-height) - 24px) / 2);
  color: var(--compare-color);
}

.build-hash-selector--base .build-hash-selector__selector-selection {
  color: var(--neutral-color);
}

.build-hash-selector__hash {
  color: var(--base-text-color);
  font-family: var(--monospace-font-family);
  font-size: var(--monospace-font-size);
}

.build-hash-selector__commit {
  flex-grow: 1;
  flex-shrink: 10;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.build-hash-selector__links {
  display: none;
  white-space: nowrap;
}

.build-hash-selector__item:hover .build-hash-selector__links {
  display: inline;
}

.build-hash-selector__links > * {
  margin: 0 calc(var(--base-spacing) / 2);
}

.build-hash-selector__branch-label {
  font-family: var(--monospace-font-family);
  font-size: var(--monospace-font-size);
  margin-left: calc(var(--base-spacing) / 2);
  margin-top: calc(var(--base-spacing) * -1);
}

.build-hash-selector__branch-label--dev-branch {
  color: var(--dev-branch-color);
}

.git-viz {
  position: relative;

  width: 50px;
  min-width: 50px;
  height: var(--line-item-height);
}

.git-viz * {
  position: absolute;
}

.git-viz__master-line {
  border-left: 2px solid var(--neutral-color);
  left: 9px;
  height: var(--line-item-height);
  width: 1px;
}

.git-viz__dev-line {
  border-left: 2px solid var(--dev-branch-color);
  left: 29px;
  height: var(--line-item-height);
  width: 1px;
}

.git-viz__master-node {
  background: var(--neutral-color);
  left: 5px;
  top: 15px;

  height: 10px;
  width: 10px;

  border-radius: 100%;
  z-index: 5;
}

.git-viz__dev-node {
  background: var(--dev-branch-color);
  left: 25px;
  top: 15px;

  height: 10px;
  width: 10px;

  border-radius: 100%;
  z-index: 5;
}

.git-viz__dev-branch-off {
  left: 11px;
  top: 0px;

  height: 20px;
  width: 20px;

  border-bottom-right-radius: 100px;
  border-right: 2px solid var(--dev-branch-color);
  border-bottom: 2px solid var(--dev-branch-color);
}
